<template>
  <div class="event-summery">
    <h3
      class="event-summery__credits"
      v-if="detail?.total_credit_hours && !hideCeInformation"
    >
      {{ detail.total_credit_hours }} Credit{{ detail.total_credit_hours > 1 ? 's' : '' }} Earned
    </h3>

    <div
      v-if="buttons?.evaluation?.status && buttons?.download_certificate?.enabled && !attendeeToCompletion"
      class="certificate-message"
    >
      <Notify theme="success">
        <h4>You can now download your certificate.</h4>

        <div v-if="isSingleSession">
          You have completed the evaluation and may now download your certificate.
        </div>
        <div v-if="!isSingleSession">
          Only sessions with completed evaluations will be included, so make sure you've
          evaluated every session you attended.
        </div>
      </Notify>
    </div>

    <div
      v-if="buttons?.evaluation?.status && !buttons?.download_certificate?.enabled"
      class="certificate-message"
    >
      <Notify theme="success">
        <div>
          Please complete session evaluations to include credits in your certificate.
        </div>
      </Notify>
    </div>

    <div
      v-if="!isFetching && !buttons?.evaluation?.status"
      class="summery-evaluation"
    >
      <div>
        <h3>Summary Evaluation</h3>
      </div>

      <div
        v-if="!buttons?.evaluation?.status && !buttons?.download_certificate?.enabled && attendeeToCompletion"
        class="certificate-message"
      >
        <Notify theme="info">
          <div>
            Partial credit is not available for this event. Participants are required to attend all sessions in order to receive CE certificate.
          </div>
        </Notify>
      </div>

      <div v-if="!attendee?.conference?.has_ended && !isSingleSession">
        <Notify theme="primary" class="certificate-message">
          <i class="fa-regular fa-circle-info"></i>
          The event evaluation will be available when the event has ended. After completing the Summary Evaluation,
          you will be able to download a {{ hideCeInformation ? '' : 'CE' }} certificate.
        </Notify>
      </div>

      <div v-if="attendee?.conference?.has_ended && !isSingleSession && buttons?.evaluation?.enabled && !attendeeToCompletion">
        <Notify theme="primary" class="certificate-message">
          <i class="fa-regular fa-circle-info"></i>
          Your event has concluded. After completing the Summary Evaluation, you will be able
          to download a CE certificate.
        </Notify>
      </div>

      <div v-if="buttons?.quiz?.is_failed" class="summary-button">
        <colored-status color="#e2544d">Quiz Failed</colored-status>
      </div>

      <div v-if="buttons?.quiz?.enabled" class="summary-button">
        <c-button
          theme="warning"
          size="sm"
          icon="fa-solid fa-ballot-check"
          uppercase
          fill
          :to="eventQuizPath"
        >Start Quiz</c-button>
      </div>

      <div v-if="buttons?.evaluation?.enabled && !buttons?.evaluation?.expired" class="summary-button">
        <span class="eval-badge-wrap">
          <c-button
            theme="primary"
            size="sm"
            icon="fa-solid fa-comments-question"
            uppercase
            fill
            :to="eventEvaluationPath"
          >Start Event Evaluation</c-button>

          <span
              v-if="buttons?.evaluation?.expiration_date"
              class="eval-expiration-badge"
              v-tooltip="'Evaluation available until\n' + buttons.evaluation.expiration_date"
          >?</span>
        </span>
      </div>

      <div
          v-if="buttons?.evaluation?.enabled && buttons?.evaluation?.expired"
          class="summary-button"
      >
        <span class="eval-badge-wrap">
          <c-button
            theme="primary"
            size="sm"
            icon="fa-solid fa-comments-question"
            uppercase
            fill
            disabled
          >Start Event Evaluation</c-button>

          <span
              class="eval-expiration-badge"
              v-tooltip="EVALUATION_EXPIRED_MESSAGE"
          >?</span>
        </span>
      </div>
    </div>

    <div v-if="buttons?.download_certificate?.enabled" class="summary-button">
      <c-button
        theme="success"
        size="sm"
        icon="fa-solid fa-file-certificate"
        uppercase
        fill
        @click="openDownloadCertificateModal"
      >Download Certificate</c-button>
    </div>

    <div v-if="buttons?.evaluation_followup?.enabled">
      <Notify theme="success" class="certificate-message">
        <h4>Event Follow Up Questions</h4>
        <div>
          Please help us by answering a few follow up questions about this activity.
        </div>
      </Notify>

      <div class="summary-button">
        <c-button
          theme="primary"
          size="sm"
          icon="fa-solid fa-comments-question"
          uppercase
          fill
          :to="eventFollowUpPath"
        >Event Follow Up</c-button>
      </div>
    </div>

    <download-certificate-form-modal
      v-if="showDownloadCertificateModal"
      @close="closeDownloadCertificateModal"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ROUTES } from "../../router";
import { SESSION_STATUSES } from "../../constants/messages";
import Notify from "../ui/Notify";
import ColoredStatus from "../ui/ColoredStatus";
import DownloadCertificateFormModal from "./download-certificate-form-modal";
import attendeeCertificateAPI from "../../api/certificateAPI";

export default {
  components: {
    DownloadCertificateFormModal,
    Notify,
    ColoredStatus,
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      showDownloadCertificateModal: false,
      detail: {},
    }
  },
  computed: {
    ...mapGetters('attendeeStore', [
      'attendee',
      'isSingleSession',
      'hideCeInformation',
      'attendeeToCompletion',
    ]),

    ...mapGetters('agendaStore', [
      'isFetching',
      'buttons',
    ]),

    eventQuizPath() {
      return ROUTES.AGENDA_QUIZ.replace(':attendeeId', this.attendeeId);
    },

    eventEvaluationPath() {
      return ROUTES.AGENDA_EVALUATION.replace(':attendeeId', this.attendeeId);
    },

    eventFollowUpPath() {
      return ROUTES.EVENT_FOLLOW_UP.replace(':attendeeId', this.attendeeId);
    },

    EVALUATION_EXPIRED_MESSAGE() {
      return SESSION_STATUSES.EVALUATION_EXPIRED;
    },
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    fetchDetail() {
      attendeeCertificateAPI.fetchCertificateDetails(this.attendeeId)
        .then((res) => {
          this.detail = res.data.data;
          this.$store.commit("agendaStore/updateAgendaButtons", { ...res.data?.data?.buttons });
        })
        .catch(() => {})
        .finally(() => {});
    },

    openDownloadCertificateModal() {
      this.showDownloadCertificateModal = true;
    },

    closeDownloadCertificateModal() {
      this.showDownloadCertificateModal = false;
    },
  }
}
</script>

<style scoped lang="scss">
.event-summery {
  &__credits {
    color: var(--attendee-text-primary-color);
    margin-bottom: var(--attendee-gap-sm);
    text-align: center;
  }
}

.certificate-message {
  margin: var(--attendee-gap-md) auto;
}

.summary-button {
  margin-bottom: var(--attendee-gap-md);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.eval-badge-wrap {
  position: relative;
  display: inline-block;
}

.eval-expiration-badge {
  position: absolute;
  top: -7px;
  right: -7px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e2544d;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  cursor: help;
  z-index: 2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .2);
}

.summery-evaluation {
  h3, h5 {
    color: var(--attendee-text-primary-color);
    margin-bottom: var(--attendee-gap-sm);
    text-align: center;
  }
}
</style>
